import { statisticalPreviewTables } from '../utils/statisticalPreview';
import { displayTimepoint } from '../utils/displayTimepoint';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { MenuUnfoldOutlined } from '@ant-design/icons';
import { Button, Checkbox, Empty, Select, Spin, Tag } from 'antd';
import useFileHandler from '../hooks/useFileHandler';
import useGraphData from '../hooks/useGraphData';
import useSelectCommunity from '../hooks/useSelectCommunity';
import { getFiles, getGraphProvenance, getStatisticalPreview } from '../services/api';
import CommunityList from './CommunityList';
import GraphViewer from './GraphViewer';
import { colorNode, contextSubtype, contextSubtypeSchema } from './GraphViewerConfig';
import './Graph.css';
import { groupTemporalGraph } from '../utils/temporalGraph';
import { contextCategories, filterGraphView, selectContextCategories } from '../utils/contextGraph';
import { statisticalGraph, statisticalMetadataOptions } from '../utils/statisticalGraph';
import { decorateNodeAttributes, filterNodeAttributes } from '../utils/nodeAttributes';
import { splitStudyCohortGraph } from '../utils/studyCohortGraph';
import { GRAPH_ATTRIBUTE_FILTER_KEYS, readGraphShareState, writeGraphShareState } from '../utils/graphShareState';

function Graph({ graphId, graphUuid, onVersionChange, onDrawerOpenChange, triggerSignal, contextCommunities, preview = false, statsOnly = false }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const shareState = useMemo(() => readGraphShareState(searchParams.toString()), [searchParams]);
  const { hideIsolatedNodes, graphMode: requestedGraphMode } = shareState;
  const statsViewOnly = statsOnly || preview;
  const graphMode = statsViewOnly ? 'statistics' : requestedGraphMode;
  const contextTypesParam = searchParams.get('context_types');
  const selectedContextSubtypes = useMemo(() => contextTypesParam === null ? null : contextTypesParam.split('|').filter(Boolean), [contextTypesParam]);
  // Selection URLs must not rebuild graph data and reset an open detail drawer.
  const typesParam = searchParams.get('types');
  const selectedCategories = useMemo(() => typesParam === null ? null : typesParam.split('|').filter(Boolean), [typesParam]);
  // Node/edge selection changes must not invalidate the filtered graph data.
  const attributeKey = JSON.stringify(GRAPH_ATTRIBUTE_FILTER_KEYS.map(key => searchParams.get(key)));
  const attributeFilters = useMemo(() => {
    const values = JSON.parse(attributeKey);
    return Object.fromEntries(GRAPH_ATTRIBUTE_FILTER_KEYS.map((key, index) => [key, values[index] || null]));
  }, [attributeKey]);
  const { method: methodParam, analysis: analysisParam, design: designParam } = attributeFilters;
  const [attributeProvenance, setAttributeProvenance] = useState(null);
  const [message, setMessage] = useState('No graph data loaded');
  const { arrayMap, setArrayMap, handleFilesRead } = useFileHandler();
  const { graphData, details } = useGraphData(arrayMap);
  const [filesReady, setFilesReady] = useState(false);
  const [selectedGraph, setSelectedGraph] = useState({});
  const [showCommunityList, setShowCommunityList] = useState(false);
  const { selectCommunity, newCommunity, selectedCommunity } = useSelectCommunity();

  useEffect(() => {
    const loadFiles = async () => {
      try {
        setFilesReady(false);
        setMessage('Retrieving graph data...');
        if (preview) {
          const result = await getStatisticalPreview(graphId, graphUuid);
          setArrayMap(statisticalPreviewTables(result));
          setAttributeProvenance(result);
        } else {
          const files = await getFiles(graphId);
          await handleFilesRead(files);
        }
        setFilesReady(true);
      } catch (error) {
        console.error('Error setting files:', error);
        setMessage('Error retrieving graph data. Please try again.');
        setFilesReady(false);
      }
    };

    if (graphId) {
      loadFiles();
    } else {
      setFilesReady(false);
      setMessage('Select a ready graph to begin.');
    }
  }, [graphId, graphUuid, preview, handleFilesRead, setArrayMap]);

  useEffect(() => {
    setShowCommunityList(true);
  }, [triggerSignal]);

  const updateShareState = useCallback(patch => {
    const next = writeGraphShareState(searchParams.toString(), { ...shareState, ...patch });
    if (next !== searchParams.toString()) setSearchParams(next, { replace: true });
  }, [searchParams, shareState, setSearchParams]);
  useEffect(() => {
    if (statsViewOnly && requestedGraphMode !== 'statistics') {
      updateShareState({ graphMode: 'statistics', selectedCategories: null, contextSubtypes: null,
        attributeFilters: {}, nodeId: null, focusNodeId: null, edge: null });
    }
  }, [statsViewOnly, requestedGraphMode, updateShareState]);
  const isV2 = graphData.nodes.some(node => (node.category_tags || [node.type]).some(category => ['Study', 'Cohort', 'Study / Cohort', 'Biomedical Entities', 'Attributes & Measurements', 'Methods & Study Design', 'Context'].includes(category)));
  const classifiedData = useMemo(() => isV2 ? splitStudyCohortGraph(graphData, attributeProvenance) : graphData, [isV2, graphData, attributeProvenance]);
  const viewData = useMemo(() => graphMode === 'statistics' ? statisticalGraph(classifiedData, { provenance: attributeProvenance }) : filterGraphView(classifiedData, { graphMode }), [classifiedData, graphMode, attributeProvenance]);
  const categories = useMemo(() => [...new Set(viewData.nodes.flatMap(node => node.category_tags || [node.type]))].filter(value => value && (graphMode !== 'context' || contextCategories.includes(value))).sort(), [viewData, graphMode]);
  useEffect(() => {
    setAttributeProvenance(null);
    if (!isV2 || !graphId) return;
    let cancelled = false;
    (preview ? getStatisticalPreview(graphId, graphUuid) : getGraphProvenance(graphId, graphUuid)).then(result => { if (!cancelled) setAttributeProvenance(result); }).catch(() => {});
    return () => { cancelled = true; };
  }, [isV2, graphId, graphUuid, preview]);
  useEffect(() => { if (isV2) setShowCommunityList(false); }, [isV2, graphId, graphUuid]);
  useEffect(() => { onVersionChange?.(isV2); }, [isV2, onVersionChange]);
  const colorByContextSubtype = isV2 && (graphMode === 'context' || (selectedCategories?.includes('Context') && selectedCategories.every(category => contextCategories.includes(category))));
  const contextSubtypes = useMemo(() => {
    const present = new Set(viewData.nodes.filter(node => (node.category_tags || [node.type]).includes('Context')).map(contextSubtype));
    return Object.keys(contextSubtypeSchema).filter(subtype => present.has(subtype));
  }, [viewData]);
  const categoryLabel = value => (
    <span className="graph-category-label">
      <span className="graph-category-marker" style={{ backgroundColor: colorNode({ type: value }) }} aria-hidden="true" />
      {value}
    </span>
  );
  const filteredData = useMemo(() => filterGraphView(viewData, { selectedCategories, contextSubtypes: selectedContextSubtypes }), [viewData, selectedCategories, selectedContextSubtypes]);

  const statisticalData = useMemo(() => isV2 && graphMode === 'statistics' ? statisticalGraph(filteredData, { method: methodParam, analysis: analysisParam, design: designParam, provenance: attributeProvenance, methodNodes: classifiedData.nodes }) : null, [isV2, graphMode, filteredData, methodParam, analysisParam, designParam, attributeProvenance, classifiedData]);
  const analysisOptions = useMemo(() => statisticalMetadataOptions(filteredData, 'analysis_type', { design: designParam }), [filteredData, designParam]);
  const designOptions = useMemo(() => statisticalMetadataOptions(filteredData, 'study_design', { analysis: analysisParam }), [filteredData, analysisParam]);
  const changeMetadataFilter = (kind, value) => {
    updateShareState({ attributeFilters: { ...attributeFilters, method: null, [kind]: value || null },
      nodeId: null, focusNodeId: null, edge: null }); setSelectedGraph({});
  };
  const attributedData = useMemo(() => isV2 ? filterNodeAttributes(decorateNodeAttributes(statisticalData || filteredData, attributeProvenance), attributeFilters) : filteredData, [isV2, statisticalData, filteredData, attributeProvenance, attributeFilters]);
  const displayData = useMemo(() => isV2 ? groupTemporalGraph(attributedData) : attributedData, [isV2, attributedData]);
  const statisticalRecordCount = statisticalData ? attributedData.links.reduce((count, link) => count + link.statisticalRecords.length, 0) : 0;
  const filterLabels = { study: 'Study', cohort: 'Cohort', timepoint: 'Timepoint',
    method: 'Method / Study design', analysis: 'Analysis method', design: 'Study design' };
  const filterDisplayValue = (kind, value) => {
    if (kind === 'timepoint') return displayTimepoint(value);
    if (kind === 'method') return classifiedData.nodes.find(node => node.id === value)?.name || value;
    const options = kind === 'analysis' ? analysisOptions : kind === 'design' ? designOptions : [];
    return options.find(option => option.value === value)?.label || value;
  };
  const applyAttributeFilter = (kind, value) => { updateShareState({ ...(kind === 'method' ? { graphMode: 'statistics' } : {}), attributeFilters: { ...attributeFilters, [kind]: value || null }, selectedCategories: null, nodeId: null, focusNodeId: null, edge: null }); setSelectedGraph({}); };
  const changeGraphMode = mode => {
    if (statsViewOnly && mode !== 'statistics') return;
    updateShareState({ graphMode: mode, selectedCategories: null, contextSubtypes: null, hideIsolatedNodes: mode !== 'context', attributeFilters: {}, nodeId: null, focusNodeId: null, edge: null });
    setSelectedGraph({});
    setShowCommunityList(false);
  };
  const selectCategories = updater => {
    const current = selectedCategories === null ? categories : selectedCategories.filter(category => categories.includes(category));
    const next = selectContextCategories(current, updater(current), categories);
    updateShareState({ selectedCategories: next.length === categories.length ? null : next });
  };

  const contextSubtypeButtons = <>
                    {contextSubtypes.map(subtype => {
                      const selected = selectedContextSubtypes === null || selectedContextSubtypes.includes(subtype);
                      return <button type="button" className="graph-category-legend-item" key={subtype}
                        aria-label={`Filter Context: ${subtype}`} aria-pressed={selected}
                        title={`${selected ? 'Hide' : 'Show'} ${subtype}`}
                        onClick={() => {
                          const active = selectedContextSubtypes === null ? contextSubtypes : selectedContextSubtypes;
                          const next = selected ? active.filter(value => value !== subtype) : [...active, subtype];
                          updateShareState({ contextSubtypes: contextSubtypes.every(value => next.includes(value)) ? null : next, nodeId: null, focusNodeId: null, edge: null });
                          setSelectedGraph({});
                        }}>
                        <span className="graph-category-label">
                          <span className="graph-category-marker" style={{ backgroundColor: contextSubtypeSchema[subtype].color }} aria-hidden="true" />
                          {subtype}
                        </span>
                      </button>;
                    })}
                    {selectedContextSubtypes !== null && <Button size="small" onClick={() => { updateShareState({ contextSubtypes: null }); setSelectedGraph({}); }}>Show all Context types</Button>}
  </>;

  const categoryFilter = (
    <div className="graph-category-legend" role="group" aria-label="Filter nodes by category">
      {categories.filter(value => graphMode !== 'context' || value !== 'Context').map(value => {
        const selected = (selectedCategories === null ? categories : selectedCategories).includes(value);
        return (
          <button
            type="button"
            className="graph-category-legend-item"
            key={value}
            aria-pressed={selected}
            aria-label={value}
            title={`${selected ? 'Hide' : 'Show'} ${value}`}
            onClick={() => selectCategories(active => {
              return active.includes(value) ? active.filter(category => category !== value) : [...active, value];
            })}
          >
            {categoryLabel(value)}
          </button>
        );
      })}
      {graphMode === 'context' && <>
        {contextSubtypeButtons}
      </>}
    </div>
  );

  return (
    <div className="graph-pane" style={{ flex: filesReady ? 3 : 1 }}>
      {filesReady && !showCommunityList ? (
        <div className="drawer-button">
          <Button
            shape="circle"
            icon={<MenuUnfoldOutlined />}
            onClick={() => setShowCommunityList(true)}
            aria-label="Open community list"
          />
        </div>
      ) : null}

      <CommunityList
        details={details}
        contextCommunities={contextCommunities}
        setSelectedGraph={setSelectedGraph}
        filesReady={filesReady}
        showCommunityList={showCommunityList}
        setShowCommunityList={setShowCommunityList}
        triggerSignal={triggerSignal}
        selectedCommunity={selectedCommunity}
        newCommunity={newCommunity}
      />

      {filesReady ? (
        <div className="graph-container">
          <GraphViewer
            provenance={attributeProvenance}
            isV2={isV2}
            statisticalView={graphMode === 'statistics'}
            searchNodesOnly={isV2 && graphMode !== 'network'}
            shareSelection={{ focusNodeId: shareState.focusNodeId, nodeId: shareState.nodeId, edge: shareState.edge }}
            onShareSelectionChange={selection => updateShareState(selection)}
            categoryFilter={isV2 ? (
              <>
                <div className="graph-mode-row" role="group" aria-label="Graph view">
                  <Button type={graphMode === 'network' ? 'primary' : 'default'} aria-pressed={graphMode === 'network'} disabled={statsViewOnly} title={statsViewOnly ? 'Available for full graphs.' : undefined} onClick={() => changeGraphMode('network')}>Network graph</Button>
                  <Button type={graphMode === 'statistics' ? 'primary' : 'default'} aria-pressed={graphMode === 'statistics'} onClick={() => changeGraphMode('statistics')}>Show statistical relations only</Button>
                  <Button type={graphMode === 'context' ? 'primary' : 'default'} aria-pressed={graphMode === 'context'} disabled={statsViewOnly} title={statsViewOnly ? 'Available for full graphs.' : undefined} onClick={() => changeGraphMode('context')}>Show context only</Button>
                </div>
                {statisticalData && <div className="statistical-metadata-filters" role="group" aria-label="Filter statistical relations">
                  {[['analysis', 'Analysis method', analysisOptions, analysisParam], ['design', 'Study design', designOptions, designParam]].map(([kind, label, options, value]) =>
                    <div className="statistical-metadata-filter" key={kind}>
                      <label htmlFor={`statistical-${kind}-filter`}>{label}</label>
                      <Select id={`statistical-${kind}-filter`} aria-label={label} showSearch allowClear
                        placeholder={`All ${kind === 'analysis' ? 'analysis methods' : 'study designs'}`}
                        value={value || undefined} optionFilterProp="label"
                        options={options.map(option => ({ value: option.value, label: `${option.label} (${option.count})` }))}
                        onChange={value => changeMetadataFilter(kind, value)} notFoundContent="No matching options" />
                    </div>)}
                </div>}
                {!!Object.values(attributeFilters).filter(Boolean).length && <div className="graph-attribute-filters" aria-label="Active attribute filters">
                  {Object.entries(attributeFilters).filter(([, value]) => value).map(([kind, value]) => <Tag key={kind} className={kind === 'timepoint' ? 'node-timepoint-tag' : undefined} closable onClose={() => applyAttributeFilter(kind, null)}>{filterLabels[kind]}: {filterDisplayValue(kind, value)}</Tag>)}
                <Button size="small" onClick={() => { updateShareState({ attributeFilters: {} }); setSelectedGraph({}); }}>Clear filters</Button>
                </div>}
                {statisticalData && <div className="graph-statistics-summary" role="status">
                  {statisticalRecordCount} statistical source records · {displayData.links.length} variable pairs
                  <span>Includes non-significant results, tested relationships and candidate mediation. Select an edge for models, statistics and source evidence. Edges do not imply causality.</span>
                </div>}
                {graphMode === 'context' && <div className="graph-statistics-summary" role="status">Context, Cohort and Study · {displayData.nodes.length} nodes · {displayData.links.length} links</div>}
                {categoryFilter}
                <div className="graph-visibility-toggles">
                <Checkbox className="graph-isolated-toggle" checked={hideIsolatedNodes} onChange={event => updateShareState({ hideIsolatedNodes: event.target.checked })}>
                  Hide isolated nodes
                </Checkbox>
                </div>
                {colorByContextSubtype && graphMode !== 'context' && (
                  <div className="graph-subtype-legend" aria-label="Context subcategory colors">
                    {contextSubtypeButtons}
                  </div>
                )}
              </>
            ) : categoryFilter}
            colorByContextSubtype={colorByContextSubtype}
            onAttributeFilter={applyAttributeFilter}
            hideIsolatedNodes={isV2 && hideIsolatedNodes}
            graphId={graphId}
            onDrawerOpenChange={onDrawerOpenChange}
            onResetGraph={() => { updateShareState({ selectedCategories: null, contextSubtypes: null, hideIsolatedNodes: graphMode !== 'context', attributeFilters: {}, focusNodeId: null, nodeId: null, edge: null }); setSelectedGraph({}); }}
            data={displayData}
            details={details}
            selectedGraph={selectedGraph}
            selectCommunity={selectCommunity}
          />
        </div>
      ) : (
        <div className="graph-empty-state">
          {message === 'Retrieving graph data...' ? (
            <div className="graph-loading-content" role="status" aria-live="polite">
              <Spin size="large" />
              <span>Loading graph data…</span>
            </div>
          ) : <Empty description={message} />}
        </div>
      )}
    </div>
  );
}

export default Graph;
